/**
 * Parse Server-Sent Events from a camo generation response stream.
 */
function parseSSEBlock(block) {
  if (!block.trim()) {
    return null;
  }

  let eventType = 'message';
  let data = '';

  for (const line of block.split('\n')) {
    if (line.startsWith('event: ')) {
      eventType = line.slice(7).trim();
    } else if (line.startsWith('data: ')) {
      data = line.slice(6);
    }
  }

  if (!data) {
    return null;
  }

  return {
    eventType,
    data: JSON.parse(data)
  };
}

/**
 * Call a camo cloud function with optional SSE streaming for step 1 analysis.
 * Falls back to JSON when the server does not return text/event-stream.
 */
export async function streamCamoDesign(url, payload, headers, callbacks = {}) {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      ...headers,
      Accept: 'text/event-stream'
    },
    body: JSON.stringify({ ...payload, stream: true })
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Generation failed: ${response.status} - ${errorText}`);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/event-stream')) {
    return response.json();
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let finalResult = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const blocks = buffer.split('\n\n');
    buffer = blocks.pop() || '';

    for (const block of blocks) {
      const parsed = parseSSEBlock(block);
      if (!parsed) {
        continue;
      }

      const { eventType, data } = parsed;

      switch (eventType) {
        case 'status':
          callbacks.onStatus?.(data);
          break;
        case 'analysis_delta':
          callbacks.onAnalysisDelta?.(data.text || '');
          break;
        case 'analysis_complete':
          callbacks.onAnalysisComplete?.(data.markdownAnalysis || '');
          break;
        case 'complete':
          finalResult = data;
          callbacks.onComplete?.(data);
          break;
        case 'error':
          throw new Error(data.error || data.details || 'Generation failed');
        default:
          break;
      }
    }
  }

  if (!finalResult) {
    throw new Error('Stream ended before a complete result was received');
  }

  return finalResult;
}
